(() => {
  const LOGO_PACK_FILENAME = 'gigi-media-kit-logo-pack.zip';
  const legacyDownloadSelector = '[data-gigi-logo-download]:not([data-gigi-logo-download-kind])';

  const getLogoPackUrl = () => {
    const script =
      document.currentScript ||
      Array.from(document.scripts).find((item) => item.src && item.src.includes('/assets/global.js'));

    if (!script || !script.src) return `/cdn/shop/t/5/assets/${LOGO_PACK_FILENAME}`;

    return new URL(LOGO_PACK_FILENAME, script.src).href;
  };

  const syncLogoPackDownload = () => {
    const logoPackUrl = getLogoPackUrl();

    document.querySelectorAll(legacyDownloadSelector).forEach((link) => {
      const label = link.querySelector('.gigi-logo-popover__label');
      const labelText = label?.textContent.trim();

      if (link.closest('.gigi-logo-popover__submenu')) return;
      if (labelText !== 'Download Logo' && labelText !== 'Download Logo Pack') return;

      link.href = logoPackUrl;
      link.download = LOGO_PACK_FILENAME;

      if (labelText === 'Download Logo') {
        label.textContent = 'Download Logo Pack';
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncLogoPackDownload, { once: true });
  } else {
    syncLogoPackDownload();
  }

  document.addEventListener('shopify:section:load', syncLogoPackDownload);
})();

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};
const focusNavigationKeys = [
  'ARROWUP',
  'ARROWDOWN',
  'ARROWLEFT',
  'ARROWRIGHT',
  'TAB',
  'ENTER',
  'SPACE',
  'ESCAPE',
  'HOME',
  'END',
  'PAGEUP',
  'PAGEDOWN',
];
let lastFocusInteractionWasKeyboard = false;
const openerInteractionModes = new WeakMap();
const pointerModalHoverSuppressionAttribute = 'data-gigi-pointer-modal-hover-suppressed';
let lastTouchInteractionAt = 0;
let clearPointerModalHoverSuppression = null;

function getOpenerInteractionTarget(target) {
  if (!(target instanceof Element)) return null;

  return target.closest('button, summary, a[href], input, select, textarea, [role="button"], [aria-haspopup]');
}

function rememberOpenerInteraction(target, mode) {
  const opener = getOpenerInteractionTarget(target);
  if (opener) openerInteractionModes.set(opener, mode);
}

function suppressPointerModalHoverUntilPointerMove(pointerType) {
  clearPointerModalHoverSuppression?.();
  if (pointerType === 'touch') return;

  const root = document.documentElement;
  root.setAttribute(pointerModalHoverSuppressionAttribute, '');

  const moveEventName = window.PointerEvent ? 'pointermove' : 'mousemove';
  const cleanup = () => {
    root.removeAttribute(pointerModalHoverSuppressionAttribute);
    window.removeEventListener(moveEventName, releaseOnPointerMove, true);
    window.removeEventListener('pointerdown', releaseOnTouch, true);
    window.removeEventListener('touchstart', releaseOnTouch, true);
    clearPointerModalHoverSuppression = null;
  };
  const releaseOnPointerMove = (event) => {
    if (!event.isTrusted || event.pointerType === 'touch') return;
    cleanup();
  };
  const releaseOnTouch = (event) => {
    if (!event.isTrusted || (event.pointerType && event.pointerType !== 'touch')) return;
    cleanup();
  };

  clearPointerModalHoverSuppression = cleanup;
  window.addEventListener(moveEventName, releaseOnPointerMove, true);
  window.addEventListener('pointerdown', releaseOnTouch, true);
  window.addEventListener('touchstart', releaseOnTouch, true);
}

window.addEventListener(
  'keydown',
  (event) => {
    if (focusNavigationKeys.includes(event.code.toUpperCase())) {
      lastFocusInteractionWasKeyboard = true;
      rememberOpenerInteraction(event.target, { type: 'keyboard' });
    }
  },
  true
);

window.addEventListener(
  'pointerdown',
  (event) => {
    lastFocusInteractionWasKeyboard = false;
    if (event.pointerType === 'touch') lastTouchInteractionAt = Date.now();
    rememberOpenerInteraction(event.target, { type: 'pointer', pointerType: event.pointerType || 'mouse' });
  },
  true
);

window.addEventListener(
  'mousedown',
  (event) => {
    lastFocusInteractionWasKeyboard = false;
    if (Date.now() - lastTouchInteractionAt < 1000) return;
    rememberOpenerInteraction(event.target, { type: 'pointer', pointerType: 'mouse' });
  },
  true
);

window.addEventListener(
  'touchstart',
  (event) => {
    lastFocusInteractionWasKeyboard = false;
    lastTouchInteractionAt = Date.now();
    rememberOpenerInteraction(event.target, { type: 'pointer', pointerType: 'touch' });
  },
  true
);

window.addEventListener(
  'click',
  (event) => {
    if (event.detail === 0) {
      lastFocusInteractionWasKeyboard = true;
      rememberOpenerInteraction(event.target, { type: 'keyboard' });
    }
  },
  true
);

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (focusNavigationKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) restoreFocusAfterModal(elementToFocus);
}

function consumeOpenerInteraction(element) {
  const openerInteraction = element ? openerInteractionModes.get(element) : null;
  if (element) openerInteractionModes.delete(element);
  return openerInteraction;
}

function suppressPointerModalHoverAfterModalClose(element) {
  const openerInteraction = consumeOpenerInteraction(element);
  if (openerInteraction?.type === 'pointer') {
    suppressPointerModalHoverUntilPointerMove(openerInteraction.pointerType);
  }
}

function restoreFocusAfterModal(element) {
  const openerInteraction = consumeOpenerInteraction(element);
  const openedWithKeyboard = openerInteraction ? openerInteraction.type === 'keyboard' : lastFocusInteractionWasKeyboard;

  if (openedWithKeyboard && element?.isConnected && typeof element.focus === 'function') {
    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }
    return;
  }

  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement) activeElement.blur();

  if (openerInteraction?.type === 'pointer') {
    suppressPointerModalHoverUntilPointerMove(openerInteraction.pointerType);
  }

  requestAnimationFrame(() => {
    if (!openedWithKeyboard && document.activeElement === element) element.blur();
  });
}

function focusWithoutRing(element) {
  if (!element) return;

  try {
    element.focus({ preventScroll: true, focusVisible: false });
  } catch (error) {
    element.focus({ preventScroll: true });
  }

  element.classList.remove('focused');
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  restoreFocusAfterModal(summaryElement);
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.previousValue = this.input.value;
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('keydown', this.onInputKeydown.bind(this));
    this.input.addEventListener('input', this.onInputTyping.bind(this));
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
    window.clearTimeout(this.quantityAnimationTimeout);
  }

  onInputKeydown(event) {
    if (!this.shouldForceInteger) return;
    if (['.', ',', 'e', 'E', '+', '-'].includes(event.key)) event.preventDefault();
  }

  onInputChange(event) {
    this.normalizeIntegerQuantity();
    this.clampQuantityToMax();
    this.validateQtyRules();
    this.animateQuantityChange();
    this.previousValue = this.input.value;
  }

  onInputTyping(event) {
    this.normalizeIntegerQuantity();
    this.clampQuantityToMax();
    this.validateQtyRules();
  }

  animateQuantityChange() {
    if (!this.input || this.input.value === this.previousValue) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.input.classList.remove('quantity__input--changing');
    void this.input.offsetWidth;
    this.input.classList.add('quantity__input--changing');

    window.clearTimeout(this.quantityAnimationTimeout);
    this.quantityAnimationTimeout = window.setTimeout(() => {
      this.input.classList.remove('quantity__input--changing');
    }, 220);
  }

  setCartQuantityLoading(button) {
    if (!this.classList.contains('cart-quantity')) return;

    if (!this.loadingNumber) {
      this.loadingNumber = document.createElement('span');
      this.loadingNumber.className = 'quantity__loading-number';
      this.loadingNumber.setAttribute('aria-hidden', 'true');
      // Keep the last confirmed quantity visible while the input holds the pending request.
      this.loadingNumber.textContent = this.input.defaultValue;
      this.append(this.loadingNumber);
    }
    this.classList.add('cart-quantity--loading');
    this.setAttribute('aria-busy', 'true');
    this.closest('.cart-item')?.classList.add('cart-item--quantity-loading');
    button.classList.add('quantity__button--loading');
    button.setAttribute('aria-busy', 'true');
  }

  clearCartQuantityLoading() {
    this.classList.remove('cart-quantity--loading');
    this.removeAttribute('aria-busy');
    this.loadingNumber?.remove();
    this.loadingNumber = null;
    delete this.input.dataset.loadingButton;
    this.querySelectorAll('.quantity__button--loading').forEach((button) => {
      button.classList.remove('quantity__button--loading');
      button.removeAttribute('aria-busy');
    });
  }

  onButtonClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const previousValue = this.input.value;

    if (
      button.name === 'minus' &&
      this.classList.contains('cart-quantity') &&
      parseInt(this.input.value) <= parseInt(this.input.dataset.min)
    ) {
      const removeButton = this.closest('.cart-item')?.querySelector('cart-remove-button');
      if (removeButton) {
        this.input.dataset.loadingButton = button.name;
        this.setCartQuantityLoading(button);
        removeButton.click();
        return;
      }
    }

    if (button.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    this.clampQuantityToMax();

    if (previousValue !== this.input.value) {
      if (this.classList.contains('cart-quantity')) {
        this.input.dataset.loadingButton = button.name;
        this.setCartQuantityLoading(button);
      }
      this.input.dispatchEvent(this.changeEvent);
    }

    if (this.input.dataset.min === previousValue && button.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  get shouldClampMax() {
    return this.input.hasAttribute('data-clamp-max');
  }

  get shouldForceInteger() {
    return this.input.hasAttribute('data-integer-only');
  }

  normalizeIntegerQuantity() {
    if (!this.shouldForceInteger || this.input.value === '') return;

    const integerValue = this.input.value.match(/^\d+/)?.[0] || '';
    if (integerValue !== this.input.value) this.input.value = integerValue;
  }

  getQuantityMax() {
    const maxValue = this.input.getAttribute('max') ?? this.input.dataset.max;
    if (maxValue === undefined || maxValue === '') return null;

    const max = Number(maxValue);
    return Number.isFinite(max) ? max : null;
  }

  getQuantityValue() {
    const value = this.input.value.trim();
    if (value === '') return null;

    const quantity = Number(value);
    return Number.isFinite(quantity) ? quantity : null;
  }

  clampQuantityToMax() {
    if (!this.shouldClampMax) return;

    const max = this.getQuantityMax();
    const value = this.getQuantityValue();
    if (max === null || value === null || value <= max) return;

    this.input.value = max.toString();
  }

  validateQtyRules() {
    const value = this.getQuantityValue();
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      const min = Number(this.input.min);
      const isAtMin = value !== null && Number.isFinite(min) && value <= min;
      buttonMinus?.classList.toggle('disabled', isAtMin);
    }
    if (this.input.max) {
      const max = this.getQuantityMax();
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      const isAtMax = value !== null && max !== null && value >= max;
      buttonPlus?.classList.toggle('disabled', isAtMax);
      buttonPlus?.toggleAttribute('disabled', this.shouldClampMax && isAtMax);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;
    this.pointerOverHandling = this.pointerOverHandling.bind(this);
    this.pointerOutHandling = this.pointerOutHandling.bind(this);

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('pointerover', this.pointerOverHandling);
    this.addEventListener('pointerout', this.pointerOutHandling);
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  isDesktopMouseHover(event) {
    return (
      event.pointerType === 'mouse' &&
      (!window.matchMedia || window.matchMedia('(min-width: 750px)').matches)
    );
  }

  pointerOverHandling(event) {
    if (!this.isDesktopMouseHover(event)) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && this.contains(relatedTarget)) return;

    this.focusInHandling(event);
  }

  pointerOutHandling(event) {
    if (!this.isDesktopMouseHover(event)) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && this.contains(relatedTarget)) return;

    this.focusOutHandling(event);
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
    this.dispatchEvent(new CustomEvent('gigiSlideshowAutoplayChanged', { detail: { playing: true } }));
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
    this.dispatchEvent(new CustomEvent('gigiSlideshowAutoplayChanged', { detail: { playing: false } }));
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class GigiInputErrorPopovers {
  constructor() {
    this.dismissDelay = 5000;
    this.dismissTimers = new WeakMap();
    this.removeTimers = new WeakMap();
    this.repositionQueued = false;
    this.inputSelector = [
      'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"]):not([type="reset"]):not([type="file"])',
      'textarea',
      'select',
    ].join(',');

    document.addEventListener('invalid', this.onInvalid.bind(this), true);
    document.addEventListener('input', this.onFieldChange.bind(this), true);
    document.addEventListener('change', this.onFieldChange.bind(this), true);
    document.addEventListener('submit', this.onSubmit.bind(this), true);
    window.addEventListener('resize', this.queueReposition.bind(this));
    window.addEventListener('scroll', this.queueReposition.bind(this), true);

    document.querySelectorAll('[aria-invalid="true"]').forEach((field) => {
      if (this.isSupportedField(field)) this.getAnchor(field).classList.add('gigi-input-error-anchor');
    });
  }

  isSupportedField(field) {
    return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement;
  }

  getAnchor(field) {
    return (
      field.closest(
        '[data-gigi-dropdown], .field, .select, .newsletter-form__field-wrapper, .recipient-fields__field, [class$="__field"], [class*="__field "]'
      ) || field.parentElement
    );
  }

  getVisualField(field) {
    return field.closest('[data-gigi-dropdown]')?.querySelector('[data-gigi-dropdown-button]') || field;
  }

  getPopoverRoot(field) {
    return field.closest('dialog[open]') || document.body;
  }

  getPopoverId(field) {
    if (!field.id) field.id = `GigiInput-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `${field.id}-gigi-error`;
  }

  setDescribedBy(field, popoverId) {
    const describedBy = field.getAttribute('aria-describedby');
    const ids = describedBy ? describedBy.split(/\s+/) : [];
    if (!ids.includes(popoverId)) ids.push(popoverId);
    field.setAttribute('aria-describedby', ids.join(' '));
  }

  removeDescribedBy(field, popoverId) {
    const describedBy = field.getAttribute('aria-describedby');
    if (!describedBy) return;

    const ids = describedBy.split(/\s+/).filter((id) => id && id !== popoverId);
    if (ids.length) {
      field.setAttribute('aria-describedby', ids.join(' '));
    } else {
      field.removeAttribute('aria-describedby');
    }
  }

  getValidationMessage(field, message) {
    if (this.isSupportedField(field) && field.validity.patternMismatch && field.dataset.gigiValidationMessage) {
      return field.dataset.gigiValidationMessage;
    }

    if (field instanceof HTMLInputElement && field.type === 'email') {
      const firstSentenceEnd = message.indexOf('. ');
      if (firstSentenceEnd !== -1) return message.slice(0, firstSentenceEnd + 1);
    }

    return message;
  }

  show(field, message, options = {}) {
    const anchor = this.getAnchor(field);
    const displayMessage = this.getValidationMessage(field, message);
    if (!anchor || !displayMessage) return;

    const markInvalid = options.markInvalid !== false;
    const dismissOnClick = options.dismissOnClick === true;
    const dismissDelay = Number.isFinite(options.dismissDelay) ? Math.max(0, options.dismissDelay) : this.dismissDelay;

    const popoverId = this.getPopoverId(field);
    let popover = document.getElementById(popoverId);

    if (!popover) {
      popover = document.createElement('div');
      popover.id = popoverId;
      popover.className = 'gigi-input-error-popover is-hiding';
      popover.setAttribute('role', 'alert');
      popover.dataset.gigiInputErrorFor = field.id;

      const icon = document.createElement('span');
      icon.className = 'gigi-input-error-popover__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '!';

      const text = document.createElement('span');
      text.className = 'gigi-input-error-popover__text';

      popover.append(icon, text);
    }

    const shouldAnimateIn = !popover.isConnected || popover.classList.contains('is-hiding');
    const popoverRoot = this.getPopoverRoot(field);
    if (popover.parentElement !== popoverRoot) popoverRoot.append(popover);

    if (options.placement) {
      popover.dataset.preferredPlacement = options.placement;
    } else {
      delete popover.dataset.preferredPlacement;
    }
    popover.classList.toggle('is-dismissible', dismissOnClick);
    popover.onclick = dismissOnClick ? () => this.hide(field, markInvalid) : null;

    const visualField = this.getVisualField(field);
    window.clearTimeout(this.dismissTimers.get(field));
    window.clearTimeout(this.removeTimers.get(field));
    popover.querySelector('.gigi-input-error-popover__text').textContent = displayMessage;
    if (markInvalid) {
      field.setAttribute('aria-invalid', 'true');
      if (visualField !== field) visualField.setAttribute('aria-invalid', 'true');
      field.dataset.gigiInputError = 'true';
      anchor.classList.add('is-invalid');
    }
    this.setDescribedBy(field, popoverId);
    anchor.classList.add('gigi-input-error-anchor');
    this.positionPopover(field, popover);
    if (shouldAnimateIn) {
      requestAnimationFrame(() => popover.classList.remove('is-hiding'));
    } else {
      popover.classList.remove('is-hiding');
    }
    this.dismissTimers.set(field, window.setTimeout(() => this.hidePopover(field), dismissDelay));
  }

  hide(field, clearInvalid = true) {
    const anchor = this.getAnchor(field);
    if (!anchor) return;
    if (!field.id) return;

    this.hidePopover(field);

    if (clearInvalid) {
      const visualField = this.getVisualField(field);
      if (field.dataset.gigiInputError) {
        field.removeAttribute('aria-invalid');
        delete field.dataset.gigiInputError;
      }
      if (visualField !== field) visualField.removeAttribute('aria-invalid');
      anchor.classList.remove('is-invalid');
    }
  }

  hidePopover(field) {
    if (!field.id) return;

    const popoverId = this.getPopoverId(field);
    const popover = document.getElementById(popoverId);
    window.clearTimeout(this.dismissTimers.get(field));
    window.clearTimeout(this.removeTimers.get(field));
    this.dismissTimers.delete(field);
    this.removeTimers.delete(field);
    this.removeDescribedBy(field, popoverId);

    if (!popover) return;

    popover.classList.add('is-hiding');
    this.removeTimers.set(
      field,
      window.setTimeout(() => {
        popover.remove();
        this.removeTimers.delete(field);
      }, 180)
    );
  }

  positionPopover(field, popover) {
    const visualField = this.getVisualField(field);
    const rect = visualField.getBoundingClientRect();
    const popoverRoot = this.getPopoverRoot(field);
    const isDialogPopover = popoverRoot.tagName === 'DIALOG';
    const boundaryRect = isDialogPopover
      ? popoverRoot.getBoundingClientRect()
      : {
          top: 0,
          right: window.innerWidth,
          bottom: window.innerHeight,
          left: 0,
        };
    const gap = 8;
    const edge = isDialogPopover ? 16 : 12;
    const boundaryLeft = boundaryRect.left + edge;
    const boundaryRight = boundaryRect.right - edge;
    const boundaryTop = boundaryRect.top + edge;
    const boundaryBottom = boundaryRect.bottom - edge;
    const maxWidth = Math.max(160, Math.min(320, boundaryRight - boundaryLeft));

    popover.dataset.layer = isDialogPopover ? 'dialog' : 'page';
    popover.style.position = isDialogPopover ? 'absolute' : 'fixed';
    popover.style.maxWidth = `${maxWidth}px`;
    popover.style.left = '0px';
    popover.style.top = '0px';

    const popoverRect = popover.getBoundingClientRect();
    const placeAbove =
      popover.dataset.preferredPlacement === 'top' ||
      (popover.dataset.preferredPlacement !== 'bottom' && rect.bottom + gap + popoverRect.height > boundaryBottom);
    const top = placeAbove ? rect.top - gap - popoverRect.height : rect.bottom + gap;
    const startLeft = rect.left;
    const endLeft = rect.right - popoverRect.width;
    const fitsStart = startLeft + popoverRect.width <= boundaryRight;
    const fitsEnd = endLeft >= boundaryLeft;
    const preferredLeft = fitsStart || !fitsEnd ? startLeft : endLeft;
    const left = Math.min(Math.max(preferredLeft, boundaryLeft), boundaryRight - popoverRect.width);
    const clampedTop = Math.max(boundaryTop, top);
    const fieldCenter = rect.left + rect.width / 2;
    const arrowLeft = Math.min(Math.max(fieldCenter - left - 7.5, 16), popoverRect.width - 31);

    popover.dataset.placement = placeAbove ? 'top' : 'bottom';
    popover.dataset.align = fitsStart || !fitsEnd ? 'start' : 'end';
    popover.style.setProperty('--gigi-error-arrow-left', `${arrowLeft}px`);

    if (isDialogPopover) {
      popover.style.left = `${left - boundaryRect.left + popoverRoot.scrollLeft}px`;
      popover.style.top = `${clampedTop - boundaryRect.top + popoverRoot.scrollTop}px`;
      return;
    }

    popover.style.left = `${left}px`;
    popover.style.top = `${clampedTop}px`;
  }

  queueReposition() {
    if (this.repositionQueued) return;

    this.repositionQueued = true;
    requestAnimationFrame(() => {
      this.repositionQueued = false;
      document.querySelectorAll('.gigi-input-error-popover').forEach((popover) => {
        const fieldId = popover.dataset.gigiInputErrorFor;
        if (!fieldId) return;

        const field = document.getElementById(fieldId);
        if (field) this.positionPopover(field, popover);
      });
    });
  }

  onInvalid(event) {
    const field = event.target;
    if (!this.isSupportedField(field)) return;

    event.preventDefault();
    this.show(field, field.validationMessage);
    if (this.invalidFocusQueued) return;

    this.invalidFocusQueued = true;
    requestAnimationFrame(() => {
      this.getVisualField(field).focus({ preventScroll: false });
      this.invalidFocusQueued = false;
    });
  }

  onFieldChange(event) {
    const field = event.target;
    if (!this.isSupportedField(field)) return;

    if (field.validity.valid) {
      this.hide(field);
      return;
    }

    if (field.id && document.getElementById(this.getPopoverId(field))) {
      this.show(field, field.validationMessage);
    }
  }

  onSubmit(event) {
    const invalidField = Array.from(event.target.querySelectorAll(this.inputSelector)).find(
      (field) => this.isSupportedField(field) && !field.validity.valid
    );

    if (!invalidField) return;

    this.show(invalidField, invalidField.validationMessage);
    requestAnimationFrame(() => this.getVisualField(invalidField).focus({ preventScroll: false }));
  }
}

class GigiFormSubmitGuard {
  constructor() {
    this.loadingLabel = 'Submitting...';
    this.submittingForms = new WeakSet();
    this.unlockTimers = new WeakMap();
    this.blockDuplicateSubmit = this.blockDuplicateSubmit.bind(this);
    this.lockNativeSubmit = this.lockNativeSubmit.bind(this);
    this.resetLockedForms = this.resetLockedForms.bind(this);

    document.addEventListener('submit', this.blockDuplicateSubmit, true);
    document.addEventListener('submit', this.lockNativeSubmit);
    window.addEventListener('pageshow', this.resetLockedForms);
  }

  shouldGuard(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.dataset.gigiAllowRepeatSubmit === 'true') return false;
    if ((form.getAttribute('method') || 'get').toLowerCase() === 'get') return false;
    if (form.closest('product-form, cart-drawer, cart-items, quick-order-list, localization-form')) return false;

    return !this.isCartAction(form);
  }

  isCartAction(form) {
    const action = form.getAttribute('action') || window.location.href;

    let actionPath = '';
    try {
      actionPath = new URL(action, window.location.origin).pathname;
    } catch (error) {
      return false;
    }

    const cartPaths = [
      window.routes?.cart_add_url,
      window.routes?.cart_change_url,
      window.routes?.cart_update_url,
      window.routes?.cart_url,
    ]
      .filter(Boolean)
      .map((path) => {
        try {
          return new URL(path, window.location.origin).pathname;
        } catch (error) {
          return path;
        }
      });

    return cartPaths.includes(actionPath);
  }

  blockDuplicateSubmit(event) {
    const form = event.target;
    if (!this.shouldGuard(form)) return;
    if (!this.submittingForms.has(form) && form.dataset.gigiSubmitting !== 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }

  lockNativeSubmit(event) {
    const form = event.target;
    if (!this.shouldGuard(form)) return;

    if (event.defaultPrevented) {
      this.unlockForm(form);
      return;
    }

    this.lockForm(form, event.submitter);
  }

  lockForm(form, submitter) {
    this.submittingForms.add(form);
    form.dataset.gigiSubmitting = 'true';
    form.setAttribute('aria-busy', 'true');

    const controls = this.getSubmitControls(form, submitter);
    const loadingControl = this.getLoadingControl(controls, submitter);

    controls.forEach((control) => {
      if (control.disabled) {
        control.dataset.gigiSubmitGuardWasDisabled = 'true';
      } else {
        control.disabled = true;
      }
      control.setAttribute('aria-disabled', 'true');
    });

    if (loadingControl) this.showLoadingState(loadingControl);

    window.clearTimeout(this.unlockTimers.get(form));
    this.unlockTimers.set(form, window.setTimeout(() => this.unlockForm(form), 15000));
  }

  unlockForm(form) {
    this.submittingForms.delete(form);
    form.removeAttribute('aria-busy');
    delete form.dataset.gigiSubmitting;
    window.clearTimeout(this.unlockTimers.get(form));
    this.unlockTimers.delete(form);

    this.getSubmitControls(form).forEach((control) => {
      this.restoreLoadingState(control);
      if (control.dataset.gigiSubmitGuardWasDisabled === 'true') {
        delete control.dataset.gigiSubmitGuardWasDisabled;
      } else {
        control.disabled = false;
      }
      control.removeAttribute('aria-disabled');
    });
  }

  getSubmitControls(form, submitter = null) {
    const controls = Array.from(form.querySelectorAll('button, input')).filter((control) => {
      if (control instanceof HTMLButtonElement) return (control.getAttribute('type') || 'submit').toLowerCase() === 'submit';
      if (control instanceof HTMLInputElement) return ['submit', 'image'].includes(control.type);
      return false;
    });

    if (submitter && !controls.includes(submitter)) controls.push(submitter);

    return controls;
  }

  getLoadingControl(controls, submitter) {
    if (submitter && controls.includes(submitter)) return submitter;

    return controls.find((control) => control instanceof HTMLButtonElement) || controls[0];
  }

  showLoadingState(control) {
    control.classList.add('gigi-submit-loading-button', 'is-loading');
    control.setAttribute('aria-live', 'polite');

    if (control instanceof HTMLInputElement) {
      if (!control.dataset.gigiSubmitGuardOriginalValue) {
        control.dataset.gigiSubmitGuardOriginalValue = control.value;
      }
      control.value = this.loadingLabel;
      return;
    }

    if (!(control instanceof HTMLButtonElement)) return;
    if (control.dataset.gigiSubmitGuardEnhanced === 'true') return;

    const originalLabel = control.textContent.replace(/\s+/g, ' ').trim();
    const fallbackLabel = control.getAttribute('aria-label') || '';
    const readyLabel = originalLabel || fallbackLabel;
    const showLoadingText = Boolean(originalLabel);

    control.dataset.gigiSubmitGuardOriginalHtml = control.innerHTML;
    control.dataset.gigiSubmitGuardOriginalAriaLive = control.getAttribute('aria-live') || '';
    control.dataset.gigiSubmitGuardOriginalAriaLabel = control.getAttribute('aria-label') || '';
    control.dataset.gigiSubmitGuardEnhanced = 'true';

    const ready = document.createElement('span');
    ready.className = 'gigi-add-to-cart-button__ready';
    ready.textContent = readyLabel;

    const loading = document.createElement('span');
    loading.className = 'gigi-add-to-cart-button__loading';
    loading.setAttribute('aria-hidden', 'true');

    const spinner = document.createElement('span');
    spinner.className = 'gigi-add-to-cart-button__spinner';

    loading.append(spinner);
    if (showLoadingText) {
      const loadingText = document.createElement('span');
      loadingText.textContent = this.loadingLabel;
      loading.append(loadingText);
    }

    control.textContent = '';
    control.append(ready, loading);
    control.setAttribute('aria-label', this.loadingLabel);
  }

  restoreLoadingState(control) {
    control.classList.remove('is-loading');

    if (control instanceof HTMLInputElement && control.dataset.gigiSubmitGuardOriginalValue) {
      control.value = control.dataset.gigiSubmitGuardOriginalValue;
      delete control.dataset.gigiSubmitGuardOriginalValue;
    }

    if (control instanceof HTMLButtonElement && control.dataset.gigiSubmitGuardEnhanced === 'true') {
      control.innerHTML = control.dataset.gigiSubmitGuardOriginalHtml || control.innerHTML;
      if (control.dataset.gigiSubmitGuardOriginalAriaLive) {
        control.setAttribute('aria-live', control.dataset.gigiSubmitGuardOriginalAriaLive);
      } else {
        control.removeAttribute('aria-live');
      }

      if (control.dataset.gigiSubmitGuardOriginalAriaLabel) {
        control.setAttribute('aria-label', control.dataset.gigiSubmitGuardOriginalAriaLabel);
      } else {
        control.removeAttribute('aria-label');
      }

      delete control.dataset.gigiSubmitGuardOriginalHtml;
      delete control.dataset.gigiSubmitGuardOriginalAriaLive;
      delete control.dataset.gigiSubmitGuardOriginalAriaLabel;
      delete control.dataset.gigiSubmitGuardEnhanced;
    }

    control.classList.remove('gigi-submit-loading-button');
  }

  resetLockedForms() {
    document.querySelectorAll('form[data-gigi-submitting="true"]').forEach((form) => this.unlockForm(form));
  }
}

class GigiPhoneNumberInputs {
  constructor() {
    this.minimumDigits = 10;
    this.tooShortMessage = 'Please enter at least 10 digits.';

    document.querySelectorAll('input[type="tel"]').forEach((field) => this.prepareField(field));
    document.addEventListener('beforeinput', this.onBeforeInput.bind(this), true);
    document.addEventListener('input', this.onInput.bind(this), true);
    document.addEventListener('change', this.onInput.bind(this), true);
    document.addEventListener('focusin', this.onFocusIn.bind(this), true);
  }

  isPhoneField(field) {
    return field instanceof HTMLInputElement && field.type === 'tel';
  }

  prepareField(field) {
    if (!this.isPhoneField(field)) return;

    field.inputMode = 'numeric';
    field.pattern = `[0-9]{${this.minimumDigits},}`;
    this.syncValidity(field);
  }

  syncValidity(field) {
    const digitCount = field.value.length;
    const isTooShort = digitCount > 0 && digitCount < this.minimumDigits;
    field.setCustomValidity(isTooShort ? this.tooShortMessage : '');
  }

  onFocusIn(event) {
    this.prepareField(event.target);
  }

  onBeforeInput(event) {
    const field = event.target;
    if (!this.isPhoneField(field) || event.isComposing || event.inputType !== 'insertText') return;
    if (event.data && /\D/.test(event.data)) event.preventDefault();
  }

  onInput(event) {
    const field = event.target;
    if (!this.isPhoneField(field)) return;

    const value = field.value;
    const digits = value.replace(/\D/g, '');
    if (value === digits) {
      this.syncValidity(field);
      return;
    }

    const selectionStart = field.selectionStart ?? value.length;
    const nextSelection = value.slice(0, selectionStart).replace(/\D/g, '').length;
    field.value = digits;
    field.setSelectionRange(nextSelection, nextSelection);
    this.syncValidity(field);
  }
}

new GigiPhoneNumberInputs();
window.gigiInputErrorPopovers = new GigiInputErrorPopovers();
new GigiFormSubmitGuard();

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
